/**
 * Dependencies.
 */
import Vue from 'vue';
import { Component } from 'vue-property-decorator';

import TrackingModel from '@/api/tracking/tracking.model';
import { TRACKING_STATUS as TrackingStatus } from '@/modules/tracking/constants';
import { trackingStore } from '@/modules/tracking/store';
import { TimelineStepType } from '@/ui/components/timeline/timeline.component';

/**
 * Mixin.
 */
@Component({
  name: 'timeline-mixin'
})
export default class extends Vue {
  /**
   * Property `trackingStatus`.
   */
  trackingStatus = TrackingStatus;

  /**
   * Getter current tracking.
   */
  get currentTracking() {
    const [first = null] = this.trackingHistory || [];

    return first;
  }

  /**
   * Getter tracking history.
   */
  get trackingHistory() {
    return trackingStore.tracking || [];
  }

  /**
   * Getter timeline.
   */
  get timeline(): TimelineStepType[] {
    const {
      CANCELED,
      COLLECTED,
      DELIVERED,
      IN_ROUTING,
      IN_SERVICE_POINT,
      IN_TREATMENT,
      NOT_COLLECTED,
      NOT_DELIVERED,
      PROCESSING,
      RETURNED
    } = this.trackingStatus;

    if (!this.trackingHistory?.length) {
      return [];
    }

    let trackings: TrackingModel[] = [...this.trackingHistory];
    let timeline: TimelineStepType[] = [];

    const processingIndex = trackings.findIndex(
      tracking => tracking?.status?.code === PROCESSING
    );

    // Only to keep most recent tracking after `PROCESSING`.
    // The `PROCESSING` step can appear multiple times.
    if (processingIndex !== -1) {
      trackings = trackings.slice(0, processingIndex + 1);
    }

    const hasInRoutingStatus =
      trackings.findIndex(tracking => tracking?.status?.code === IN_ROUTING) !==
      -1;

    const { status: { code: currentStatusCode = '' } = {} } =
      this.currentTracking || {};

    switch (currentStatusCode) {
      case PROCESSING:
        timeline = [
          TimelineStepType.Current,
          TimelineStepType.Todo,
          TimelineStepType.Todo
        ];
        break;

      case IN_ROUTING:
        timeline = [
          TimelineStepType.Completed,
          TimelineStepType.Current,
          TimelineStepType.Todo
        ];
        break;

      case COLLECTED:
      case DELIVERED:
        // Success delivery.
        timeline = [
          TimelineStepType.Completed,
          TimelineStepType.Completed,
          TimelineStepType.Completed
        ];
        break;

      case NOT_COLLECTED:
      case NOT_DELIVERED:
      case IN_TREATMENT:
        // Unsuccess delivery.
        timeline = [
          TimelineStepType.Completed,
          TimelineStepType.Completed,
          TimelineStepType.Current
        ];
        break;

      case RETURNED:
      case CANCELED:
        if (hasInRoutingStatus) {
          // Delivery went on route.
          // There are 3 steps on the timeline.
          timeline = [
            TimelineStepType.Completed,
            TimelineStepType.Completed,
            TimelineStepType.Current
          ];
        } else {
          // Delivery did not go on route.
          // Only have two steps on timeline.
          timeline = [TimelineStepType.Completed, TimelineStepType.Current];
        }
        break;

      case IN_SERVICE_POINT:
        // Delivery went on route to be delivered in service point.
        // There are 3 steps on the timeline.
        timeline = [
          TimelineStepType.Completed,
          TimelineStepType.Completed,
          TimelineStepType.Current
        ];
        break;
      default:
        timeline = [
          TimelineStepType.Todo,
          TimelineStepType.Todo,
          TimelineStepType.Todo
        ];
        break;
    }

    return timeline;
  }
}
